import React, { useState, useRef, useEffect } from "react";
import { RichText } from "@shopify/hydrogen-react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { PdpTabData, PdpIngredientSection } from "@/store";
import { IngredientDrawer } from "./IngredientsDrawer";
import { Button } from "@/components/Button";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

type PdpTabProps = {
  tabData: PdpTabData;
  ingredientTabDrawerData?: PdpIngredientSection | undefined;
};

export const PdpTab = ({ tabData, ingredientTabDrawerData }: PdpTabProps) => {
  const { overview, impact, ingredients } = tabData;
  const [value, setValue] = useState(0);
  const [openIngredientDrawer, setIngredientDrawer] = React.useState(false);
  const tabPanelContainerRef = useRef<HTMLDivElement>(null);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (tabPanelContainerRef.current) {
      const currentPanel =
        tabPanelContainerRef.current.querySelector<HTMLElement>(
          ".tabpanel-visible"
        );
      if (currentPanel) {
        tabPanelContainerRef.current.style.height = `${currentPanel.scrollHeight}px`;
      }
    }
  }, [value]);

  return (
    <Box sx={{ width: "100%", paddingTop: "1rem" }}>
      <Tabs
        value={value}
        onChange={handleChange}
        aria-label="Pdp overview tab"
        className="min-h-[40px]"
        classes={{
          flexContainer:
            "space-x-10 border-0 border-b-2 border-solid border-primary-100",
        }}
        indicatorColor="primary"
      >
        {overview && (
          <Tab
            label="OVERVIEW"
            disableRipple
            {...a11yProps(0)}
            className="font-myriad-pro-condensed text-xl font-normal uppercase text-gray-500 hover:text-primary focus-visible:bg-primary-50 focus-visible:text-primary"
            style={{
              minWidth: "auto",
              minHeight: "auto",
              height: "auto",
              padding: "8px 0",
              letterSpacing: 0,
            }}
          />
        )}
        {ingredients && (
          <Tab
            label="INGREDIENTS"
            disableRipple
            {...a11yProps(1)}
            className="font-myriad-pro-condensed text-xl font-normal uppercase text-gray-500 hover:text-primary focus-visible:bg-primary-50 focus-visible:text-primary"
            style={{
              minWidth: "auto",
              minHeight: "auto",
              height: "auto",
              padding: "8px 0",
              letterSpacing: 0,
            }}
          />
        )}
        {impact && (
          <Tab
            label="IMPACT"
            {...a11yProps(2)}
            disableRipple
            className="font-myriad-pro-condensed text-xl font-normal uppercase text-gray-500 hover:text-primary focus-visible:bg-primary-50 focus-visible:text-primary"
            style={{
              minWidth: "auto",
              minHeight: "auto",
              height: "auto",
              padding: "8px 0",
              letterSpacing: 0,
            }}
          />
        )}
      </Tabs>
      <div
        ref={tabPanelContainerRef}
        style={{ overflow: "hidden", transition: "height 0.4s ease-in-out" }}
      >
        {overview && (
          <CustomTabPanel value={value} index={0}>
            <RichText
              data={overview}
              components={{
                list({ node }) {
                  return node.listType === "unordered" ? (
                    <ul className="list-disc space-y-3 pl-5 text-md text-gray-800">
                      {node.children}
                    </ul>
                  ) : (
                    <ol className="list-decimal space-y-3 pl-5 text-md text-gray-800">
                      {node.children}
                    </ol>
                  );
                },
                listItem({ node }) {
                  return <li className="list-[inherit]">{node.children}</li>;
                },
              }}
            />
          </CustomTabPanel>
        )}
        {ingredients && (
          <CustomTabPanel value={value} index={1}>
            <RichText
              data={ingredients}
              components={{
                list({ node }) {
                  return node.listType === "unordered" ? (
                    <ul className="list-disc space-y-3 pl-5 text-md text-gray-800">
                      {node.children}
                    </ul>
                  ) : (
                    <ol className="list-decimal space-y-3 pl-5 text-md text-gray-800">
                      {node.children}
                    </ol>
                  );
                },
                listItem({ node }) {
                  return <li className="list-[inherit]">{node.children}</li>;
                },
              }}
            />
            {ingredientTabDrawerData &&
              ingredientTabDrawerData.ingredientSectionDrawerData && (
                <>
                  <Button
                    variant="outline"
                    className="mt-3"
                    onClick={() => {
                      setIngredientDrawer(true);
                    }}
                  >
                    SEE ALL INGREDIENTS
                  </Button>
                  <IngredientDrawer
                    drawerData={{
                      ingredientDisclosureLink:
                        ingredientTabDrawerData.ingredientDisclosureUrl ?? "",
                      ...ingredientTabDrawerData.ingredientSectionDrawerData,
                    }}
                    open={openIngredientDrawer}
                    onExit={() => {
                      setIngredientDrawer(false);
                    }}
                  />
                </>
              )}
          </CustomTabPanel>
        )}
        {impact && (
          <CustomTabPanel value={value} index={2}>
            <RichText
              data={impact}
              components={{
                list({ node }) {
                  return node.listType === "unordered" ? (
                    <ul className="list-disc space-y-3 pl-5 text-md text-gray-800">
                      {node.children}
                    </ul>
                  ) : (
                    <ol className="list-decimal space-y-3 pl-5 text-md text-gray-800">
                      {node.children}
                    </ol>
                  );
                },
                listItem({ node }) {
                  return <li className="list-[inherit]">{node.children}</li>;
                },
              }}
            />
          </CustomTabPanel>
        )}
      </div>
    </Box>
  );
};

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const panelContainer = document.getElementById("tab-panel-container");
    if (panelRef.current && panelContainer) {
      const height = panelRef.current.scrollHeight;
      panelContainer.style.height = `${height}px`;
    }
  }, [value]);

  return (
    <div
      ref={panelRef}
      role="tabpanel"
      hidden={value !== index}
      id={`pdp-tabpanel-${index}`}
      aria-labelledby={`pdp-tab-${index}`}
      {...other}
      className={`p-4 pt-6 ${value === index ? "tabpanel-visible" : ""}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `pdp-tab-${index}`,
    "aria-controls": `pdp-tabpanel-${index}`,
  };
}
